/**
 * Background-tab-safe timers.
 *
 * Browsers aggressively throttle `setTimeout`/`setInterval` on the main thread
 * once a tab is hidden (Chrome clamps to ~1 wake-up per minute after a tab has
 * been backgrounded for a while). That breaks long-running loops such as bulk
 * message sending, where a 20s delay can balloon into many minutes — looking to
 * the user as if sending "stopped" when they switch tabs.
 *
 * Timers running inside a dedicated Web Worker are NOT subject to that
 * throttling, so we run the actual `setTimeout` in a worker and resolve a
 * promise on the main thread when it fires. The worker is created lazily from an
 * inline Blob so it needs no separate file / bundler (worker-loader) config.
 */

let worker: Worker | null = null;
let nextTimerId = 1;
const pending = new Map<number, () => void>();

const WORKER_SOURCE = `
  const timers = {};
  self.onmessage = function (e) {
    const { id, ms, action } = e.data;
    if (action === 'start') {
      timers[id] = setTimeout(function () {
        delete timers[id];
        self.postMessage({ id });
      }, ms);
    } else if (action === 'clear') {
      if (timers[id]) {
        clearTimeout(timers[id]);
        delete timers[id];
      }
    }
  };
`;

function getWorker(): Worker | null {
  if (typeof Worker === "undefined") return null;
  if (worker) return worker;

  try {
    const blob = new Blob([WORKER_SOURCE], { type: "application/javascript" });
    worker = new Worker(URL.createObjectURL(blob));
    worker.onmessage = (e: MessageEvent<{ id: number }>) => {
      const resolve = pending.get(e.data.id);
      if (resolve) {
        pending.delete(e.data.id);
        resolve();
      }
    };
  } catch {
    worker = null;
  }

  return worker;
}

/**
 * Like `sleep(ms)` but immune to background-tab timer throttling.
 *
 * Returns the promise plus a `cancel()` that resolves it immediately (used so a
 * paused/aborted send doesn't keep waiting out a long cooldown).
 */
export function workerSleep(ms: number): { promise: Promise<void>; cancel: () => void } {
  const w = getWorker();

  // Fallback to a normal timer if Workers are unavailable (e.g. SSR / old env).
  if (!w) {
    let timeoutId: ReturnType<typeof setTimeout>;
    let resolveFn: () => void;
    const promise = new Promise<void>((resolve) => {
      resolveFn = resolve;
      timeoutId = setTimeout(resolve, ms);
    });
    return {
      promise,
      cancel: () => {
        clearTimeout(timeoutId);
        resolveFn?.();
      }
    };
  }

  const id = nextTimerId++;
  const promise = new Promise<void>((resolve) => {
    pending.set(id, resolve);
    w.postMessage({ id, ms, action: "start" });
  });

  return {
    promise,
    cancel: () => {
      const resolve = pending.get(id);
      if (resolve) {
        pending.delete(id);
        w.postMessage({ id, action: "clear" });
        resolve();
      }
    }
  };
}
